import React, { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Clock } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface DateTimePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  label?: string;
  readOnly?: boolean;
}

// Generate hour and minute options
const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

export function DateTimePicker({
  value = "",
  onChange,
  placeholder = "Select date and time",
  disabled = false,
  required = false,
  className,
  id,
  label,
  readOnly = false,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Parse the current value
  const currentDate = value ? new Date(value) : undefined;
  const selectedDate = currentDate && !isNaN(currentDate.getTime()) ? currentDate : undefined;

  const currentHour = selectedDate ? selectedDate.getHours().toString().padStart(2, '0') : "09";
  const currentMinute = selectedDate ? selectedDate.getMinutes().toString().padStart(2, '0') : "00";

  const [tempHour, setTempHour] = useState(currentHour);
  const [tempMinute, setTempMinute] = useState(currentMinute);

  const handleDateSelect = (date: Date | undefined) => {
    if (date && onChange) {
      // Create a new date with the selected date and current/temp time
      const newDate = new Date(date);
      newDate.setHours(parseInt(tempHour), parseInt(tempMinute), 0, 0);

      // Format as ISO string for datetime-local compatibility
      const localDatetime = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

      onChange(localDatetime);
    }
  };

  const handleTimeChange = (hour?: string, minute?: string) => {
    const newHour = hour ?? tempHour;
    const newMinute = minute ?? tempMinute;

    setTempHour(newHour);
    setTempMinute(newMinute);

    if (selectedDate && onChange) {
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(newHour), parseInt(newMinute), 0, 0);

      const localDatetime = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      onChange(localDatetime);
    }
  };

  const formatDisplayValue = () => {
    if (!selectedDate) return "";
    return format(selectedDate, "PPP 'at' h:mm a");
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <Popover open={isOpen && !readOnly && !disabled} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !selectedDate && "text-muted-foreground",
              readOnly && "bg-muted cursor-default",
              className
            )}
            disabled={disabled}
            onClick={() => !readOnly && !disabled && setIsOpen(true)}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedDate ? formatDisplayValue() : placeholder}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 border-b">
            <h4 className="font-medium leading-none mb-3">Select Date & Time</h4>

            {/* Time Selection */}
            <div className="flex items-center space-x-2 mb-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Select value={tempHour} onValueChange={(value) => handleTimeChange(value, undefined)}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {hours.map((hour) => (
                    <SelectItem key={hour} value={hour}>
                      {hour}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-muted-foreground">:</span>
              <Select value={tempMinute} onValueChange={(value) => handleTimeChange(undefined, value)}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {minutes.filter((_, i) => i % 5 === 0).map((minute) => (
                    <SelectItem key={minute} value={minute}>
                      {minute}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quick Time Buttons */}
            <div className="flex flex-wrap gap-2">
              {["09:00", "12:00", "14:00", "18:00"].map((time) => (
                <Button
                  key={time}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const [hour, minute] = time.split(":");
                    handleTimeChange(hour, minute);
                  }}
                  className="text-xs"
                >
                  {time}
                </Button>
              ))}
            </div>
          </div>

          {/* Calendar */}
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            disabled={(date: Date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
            initialFocus
            className="border-0"
          />

          <div className="p-3 border-t">
            <Button
              className="w-full"
              onClick={() => setIsOpen(false)}
            >
              Done
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Hidden input for form compatibility */}
      <input
        type="hidden"
        name={id}
        value={value}
        required={required}
      />
    </div>
  );
}